import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock, CheckCircle2, Info } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import {
  DeductionCapBreachView,
  ProbationWarningView,
  EmiratizationSummaryView,
  probationLegalReferenceFor,
} from '../../../../models/payroll';
import { useComplianceCountry } from '../../../../hooks/useComplianceCountry';
import { EmiratizationCard } from '../components/EmiratizationCard';
import { LawReference } from '../components/LawReference';
import { FixLink } from '../../../../components/ui/FixLink';
import { appDate } from '../../../../utils/appDateFormat';

interface ComplianceIssuesTabProps {
  breaches: DeductionCapBreachView[];
  probationWarnings: ProbationWarningView[];
  emiratization: EmiratizationSummaryView | null;
  emiratizationLoading?: boolean;
}

type Severity = 'Critical' | 'High' | 'Medium' | 'Low';

const severityClass: Record<Severity, string> = {
  Critical: 'bg-red-100 text-red-700',
  High: 'bg-orange-100 text-orange-700',
  Medium: 'bg-amber-100 text-amber-700',
  Low: 'bg-blue-100 text-blue-700',
};

const breachSeverity = (b: DeductionCapBreachView): Severity => {
  const gross = b.grossSalary || 0;
  if (gross <= 0) return 'High';
  const overGrossPct = b.breachAmount / gross;
  if (overGrossPct >= 0.25) return 'Critical';
  if (overGrossPct >= 0.1) return 'High';
  return 'Medium';
};

const probationSeverity = (w: ProbationWarningView): Severity => {
  if (w.daysOverLimit > 60) return 'Critical';
  if (w.daysOverLimit > 30) return 'High';
  if (w.daysOverLimit > 0) return 'Medium';
  // Approaching-overrun: a hard legal deadline, so treat as time-sensitive.
  return 'High';
};

const formatDate = (iso: string) => appDate(iso);

export const ComplianceIssuesTab: React.FC<ComplianceIssuesTabProps> = ({
  breaches,
  probationWarnings,
  emiratization,
  emiratizationLoading,
}) => {
  const { t } = useTranslation();
  const country = useComplianceCountry();

  return (
    <div className="space-y-4">
      {/* Explainer banner — mirrors EventLog tab */}
      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 flex items-start gap-3">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
        <div className="flex-1 text-sm">
          <p className="font-medium">
            {t(
              'payroll.complianceCenter.issues.explainerTitle',
              'Live Compliance Issues'
            )}
          </p>
          <p className="text-blue-800/90 mt-0.5">
            {t(
              'payroll.complianceCenter.issues.explainerBody',
              'These are open issues based on current data. Severity is computed from how far each case is past the legal threshold. Resolve them in the relevant module. These tables refresh on the next page load.'
            )}
          </p>
        </div>
      </div>

      {country === 'UAE' && <EmiratizationCard summary={emiratization} loading={emiratizationLoading} />}

      {/* Deduction cap breaches */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
            <h3 className="font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              {t(
                'payroll.compliance.deductionCapBreaches',
                'Deduction Cap Breaches (50% Rule - Art. 25)'
              )}
            </h3>
            <div className="flex items-center gap-2">
              {breaches.length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  {t('payroll.complianceCenter.issues.openCount', '{{count}} open', {
                    count: breaches.length,
                  })}
                </span>
              )}
              <LawReference
                citations={[
                  {
                    code: 'FDL 33/2021, Art. 25',
                    description: t(
                      'payroll.complianceCenter.lawRef.deductionCap',
                      "Total deductions from an employee's wage shall not exceed 50% of gross salary. Court-ordered deductions are exempt from this cap."
                    ),
                  },
                ]}
              />
            </div>
          </div>

          {breaches.length === 0 ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded-md text-green-700 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              {t('payroll.compliance.noBreaches', 'No deduction cap breaches found for this period.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="text-start py-2 font-medium">
                      {t('common.employee', 'Employee')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.compliance.severity', 'Severity')}
                    </th>
                    <th className="text-end py-2 font-medium">
                      {t('payroll.compliance.grossSalary', 'Gross Salary')}
                    </th>
                    <th className="text-end py-2 font-medium">
                      {t('payroll.compliance.totalDeductions', 'Total Deductions')}
                    </th>
                    <th className="text-end py-2 font-medium">
                      {t('payroll.complianceCenter.issues.deductionPct', '% of Gross')}
                    </th>
                    <th className="text-end py-2 font-medium">
                      {t('payroll.compliance.cap', '50% Cap')}
                    </th>
                    <th className="text-end py-2 font-medium">
                      {t('payroll.compliance.excess', 'Excess')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.complianceCenter.issues.actionRequired', 'Action Required')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {breaches.map((b, i) => {
                    const sev = breachSeverity(b);
                    const gross = b.grossSalary || 0;
                    const pct = gross > 0 ? (b.totalDeductions / gross) * 100 : 0;
                    const pctClamp = Math.min(pct, 100);
                    return (
                      <React.Fragment key={i}>
                        <tr className="border-b border-border last:border-0 align-top">
                          <td className="py-2">{b.employeeName}</td>
                          <td className="py-2">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityClass[sev]}`}
                            >
                              {t(`payroll.compliance.severities.${sev.toLowerCase()}`, sev)}
                            </span>
                          </td>
                          <td className="py-2 text-end font-mono">
                            {(b.grossSalary ?? 0).toFixed(2)}
                          </td>
                          <td className="py-2 text-end font-mono text-red-600">
                            {(b.totalDeductions ?? 0).toFixed(2)}
                          </td>
                          <td className="py-2">
                            <div className="flex items-center justify-end gap-2">
                              <div
                                className="w-20 h-1.5 rounded-full bg-muted overflow-hidden"
                                aria-hidden
                              >
                                <div
                                  className={`h-full ${
                                    pct > 50 ? 'bg-red-500' : pct > 35 ? 'bg-amber-500' : 'bg-emerald-500'
                                  }`}
                                  style={{ width: `${pctClamp}%` }}
                                />
                              </div>
                              <span className="font-mono text-xs whitespace-nowrap">
                                {pct.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="py-2 text-end font-mono">
                            {(b.cap ?? 0).toFixed(2)}
                          </td>
                          <td className="py-2 text-end font-mono text-red-600 font-semibold">
                            {(b.breachAmount ?? 0).toFixed(2)}
                          </td>
                          <td className="py-2 text-xs text-muted-foreground max-w-xs">
                            <p>
                              {t(
                                'payroll.complianceCenter.issues.deductionAction',
                                'Reduce or split non-court deductions until total ≤ 50% of gross.'
                              )}
                            </p>
                            <FixLink
                              to={`/profile/${b.userId}/payroll/adjustments`}
                              label={t('payroll.complianceCenter.issues.fixAdjustments', 'Fix in Adjustments')}
                              testId={`fix-deduction-${b.userId}`}
                              className="mt-1"
                            />
                          </td>
                        </tr>
                        {(b.courtOrderedDeductions ?? 0) > 0 && (
                          <tr className="border-b border-border last:border-0 bg-blue-50/40 text-xs">
                            <td className="py-1 ps-4 text-blue-700" colSpan={3}>
                              <span className="inline-flex items-center gap-1 flex-wrap">
                                <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-medium">
                                  {t('payroll.compliance.courtOrderExempt', 'Court-ordered (exempt)')}
                                </span>
                                <span className="text-muted-foreground">
                                  {t('payroll.compliance.art25Exception', 'FDL Art. 25 exception')}
                                </span>
                              </span>
                            </td>
                            <td className="py-1 text-end font-mono text-blue-700" colSpan={5}>
                              {(b.courtOrderedDeductions ?? 0).toFixed(2)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Probation warnings */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
            <h3 className="font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              {t(
                'payroll.compliance.probationWarnings',
                'Probation Period Warnings'
              )}
            </h3>
            <div className="flex items-center gap-2">
              {probationWarnings.length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  {t('payroll.complianceCenter.issues.openCount', '{{count}} open', {
                    count: probationWarnings.length,
                  })}
                </span>
              )}
              <LawReference
                citations={[
                  {
                    // Use the row's legal reference; fall back to the tenant's country citation when empty.
                    code: probationWarnings[0]?.legalReference ?? probationLegalReferenceFor(country),
                    description: t(
                      'payroll.complianceCenter.lawRef.probation',
                      'Probation period shall not exceed 6 months. After this, the employee is considered confirmed and EOS / notice rules apply.'
                    ),
                  },
                ]}
              />
            </div>
          </div>

          {probationWarnings.length === 0 ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded-md text-green-700 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              {t('payroll.compliance.noProbationIssues', 'No probation period concerns.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="text-start py-2 font-medium">
                      {t('common.employee', 'Employee')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.compliance.severity', 'Severity')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.compliance.hireDate', 'Hire Date')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.complianceCenter.issues.probationEnd', 'Probation End')}
                    </th>
                    <th className="text-end py-2 font-medium">
                      {t('payroll.compliance.daysOnProbation', 'Days on Probation')}
                    </th>
                    <th className="text-end py-2 font-medium">
                      {t('payroll.compliance.daysOverLimit', 'Days Over Limit')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.complianceCenter.issues.actionRequired', 'Action Required')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {probationWarnings.map((w, i) => {
                    const sev = probationSeverity(w);
                    const overLimit = w.daysOverLimit > 0;
                    const isMisconfig = w.reason === 'ExceedsLegalMax';
                    return (
                      <tr key={i} className="border-b border-border last:border-0 align-top">
                        <td className="py-2">{w.employeeName}</td>
                        <td className="py-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityClass[sev]}`}
                          >
                            {t(`payroll.compliance.severities.${sev.toLowerCase()}`, sev)}
                          </span>
                        </td>
                        <td className="py-2">{formatDate(w.hireDate)}</td>
                        <td className="py-2">
                          {w.probationEndDate ? formatDate(w.probationEndDate) : '—'}
                        </td>
                        <td className="py-2 text-end font-mono">{w.daysOnProbation}</td>
                        <td
                          className={`py-2 text-end font-mono ${
                            overLimit ? 'text-red-600 font-semibold' : 'text-muted-foreground'
                          }`}
                        >
                          {overLimit ? w.daysOverLimit : '—'}
                        </td>
                        <td className="py-2 text-xs text-muted-foreground max-w-xs">
                          <p>
                            {isMisconfig
                              ? t(
                                  'payroll.complianceCenter.issues.probationActionMisconfig',
                                  'Configured probation is {{days}} days over the legal maximum of {{max}} days. Shorten the Probation End Date to comply.',
                                  { days: w.daysOverLimit, max: w.maxAllowedDays }
                                )
                              : overLimit
                              ? t(
                                  'payroll.complianceCenter.issues.probationActionOver',
                                  'Probation period expired. Confirm employment or end the contract. Confirmed status applies by law.'
                                )
                              : t(
                                  'payroll.complianceCenter.issues.probationActionApproaching',
                                  'Plan confirmation or termination decision before probation end date.'
                                )}
                          </p>
                          <p className="mt-0.5 text-[11px] text-muted-foreground/80">
                            {t('payroll.complianceCenter.issues.legalRef', 'Legal basis: {{ref}}', { ref: w.legalReference })}
                          </p>
                          <FixLink
                            to={`/profile/${w.userId}/personal`}
                            label={t('payroll.complianceCenter.issues.fixProfile', 'Open employee profile')}
                            testId={`fix-probation-${w.userId}`}
                            className="mt-1"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
